<script setup lang="ts">
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from '~/components/ui/select'
import { CurrencyIcon } from '~/components/ui/currency-icon'
import type { IMultiselectOption } from '~/types'

const ANY_CURRENCY = 'any'

const props = defineProps<{
  amount: number | undefined
  currency: string
  currencyOptions: IMultiselectOption[]
  hasError?: boolean
  height?: string
}>()

const emit = defineEmits<{
  (e: 'update:amount', value: number | undefined): void
  (e: 'update:currency', value: string): void
}>()

const { t } = useI18n()

const isCurrencySelected = computed(
  () => !!props.currency && props.currency !== ANY_CURRENCY
)

const selectedCurrencyLabel = computed(
  () => props.currencyOptions.find((c) => c.value === props.currency)?.label || props.currency
)

const amountPlaceholder = computed(() =>
  isCurrencySelected.value ? t('common.amount') : t('common.choose_currency')
)

const amountAriaLabel = computed(() => {
  if (!isCurrencySelected.value) return t('common.choose_currency')
  return `${t('common.amount')} (${t('common.currency')}: ${selectedCurrencyLabel.value})`
})

const onAmountUpdate = (val: string | number) => {
  if (!val || val === '') {
    emit('update:amount', undefined)
    return
  }
  const normalized = String(val).replace(',', '.')
  const num = parseFloat(normalized)
  emit('update:amount', isNaN(num) ? undefined : num)
}
</script>

<template>
  <div
    class="flex items-center gap-0 overflow-hidden rounded-lg border bg-white ring-offset-2 focus-within:ring-2"
    :class="[
      props.height ?? 'h-[2.25rem]',
      props.hasError
        ? 'border-red-500 !bg-red-50 focus-within:ring-red-500'
        : 'border-gray-300 focus-within:ring-blue-600'
    ]"
  >
    <Input
      :model-value="props.amount ?? ''"
      type="number"
      step="any"
      class="flex-1 rounded-none border-0 bg-transparent pl-4 shadow-none outline-none ring-0 ring-offset-0 focus:shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
      :class="props.hasError ? 'border-red-50' : ''"
      :placeholder="amountPlaceholder"
      :aria-label="amountAriaLabel"
      :aria-invalid="props.hasError ? 'true' : 'false'"
      :disabled="!isCurrencySelected"
      @update:model-value="onAmountUpdate"
    />
    <Select
      :model-value="props.currency"
      @update:model-value="(val) => emit('update:currency', val)"
    >
      <SelectTrigger
        class="w-[100px] rounded-none border-0 border-l bg-transparent shadow-none ring-0 ring-offset-0 focus:shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        :aria-label="$t('common.currency')"
        role="combobox"
      >
        <SelectValue class="text-base" :placeholder="$t('common.currency')" />
      </SelectTrigger>

      <SelectContent>
        <SelectItem v-for="item in props.currencyOptions" :key="item.value" :value="item.value">
          <div class="flex items-center gap-2">
            <CurrencyIcon v-if="item.value !== ANY_CURRENCY" :code="item.value" size="sm" country />
            {{ item.label }}
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
</template>
